// client/src/services/taxApi.js
// Tax Center API (server/routes/taxRoutes.js, taxMemberRoutes.js,
// taxPayeeRoutes.js). Staff calls ride the session cookie and never send a
// tenant id — the server takes it from the session. Member calls ride the
// memberAuthToken cookie, with the legacy Bearer token as a fallback (same as
// MyTicketsPage). Responses never contain a TIN or EIN, only last 4 digits.

import { postJson, putJson } from './http';

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
const base = '/api/tax';

// http.js getJson/delJson drop the error body; the Tax Center needs it
// (403 role message, 409 MIGRATION_NOT_APPLIED).
async function staff(method, url) {
  const res = await fetch(`${BASE_URL}${url}`, { method, credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || `${res.status}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}
const getJson = (url) => staff('GET', url);
const delJson = (url) => staff('DELETE', url);

// ---- admin (owners / admins)
export const getOverview = (year) => getJson(`${base}/overview${year ? `?year=${Number(year)}` : ''}`);
export const getProfile = () => getJson(`${base}/profile`);
export const saveProfile = (body) => putJson(`${base}/profile`, body);
export const startRun = (year, mode) => postJson(`${base}/runs`, { year: Number(year), mode });
export const listRuns = (year) => getJson(`${base}/runs?year=${Number(year)}`);
export const listDocuments = (year, { scope, type, recipient, drafts, page } = {}) => {
  const q = new URLSearchParams({ year: String(Number(year)) });
  if (scope) q.set('scope', scope);
  if (type) q.set('type', type);
  if (recipient) q.set('recipient', recipient);
  if (drafts) q.set('drafts', '1');
  if (page) q.set('page', String(page));
  return getJson(`${base}/documents?${q}`);
};
export const correctDocument = (id) => postJson(`${base}/documents/${Number(id)}/correct`, {});
export const documentUrl = (id, inline = false) => `${BASE_URL}${base}/documents/${Number(id)}${inline ? '?inline=1' : ''}`;
export const adminZipUrl = (year, { scope, drafts } = {}) => {
  const q = new URLSearchParams({ year: String(Number(year)) });
  if (scope) q.set('scope', scope);
  if (drafts) q.set('drafts', '1');
  return `${BASE_URL}${base}/zip?${q}`;
};
export const listWinners = (year) => getJson(`${base}/winners?year=${Number(year)}`);
export const saveWinnerPrize = (id, body) => putJson(`${base}/winners/${Number(id)}/prize`, body);
export const requestWinnerTin = (id) => postJson(`${base}/winners/${Number(id)}/request-tin`, {});
export const saveWithholding = (id, body) => putJson(`${base}/winners/${Number(id)}/withholding`, body);
export const setPrizeReleased = (id, released) => putJson(`${base}/winners/${Number(id)}/released`, { released });
export const getFmv = (year) => getJson(`${base}/fmv?year=${Number(year)}`);
export const saveTierFmv = (id, body) => putJson(`${base}/tiers/${Number(id)}/fmv`, body);
export const saveAuctionFmv = (id, fmvCents) => putJson(`${base}/auctions/${Number(id)}/fmv`, { fmvCents });
export const saveRafflePrize = (id, body) => putJson(`${base}/raffles/${Number(id)}/prize`, body);
export const listAuctionWinners = (year) => getJson(`${base}/auction-winners?year=${Number(year)}`);
export const markAuctionPaid = (id, body) => putJson(`${base}/auction-winners/${Number(id)}/paid`, body);
export const clearAuctionPaid = (id) => delJson(`${base}/auction-winners/${Number(id)}/paid`);

// ---- member portal
function memberInit() {
  const token = localStorage.getItem('memberToken');
  return { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} };
}

async function memberJson(url) {
  const res = await fetch(`${BASE_URL}${url}`, memberInit());
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body;
}

export const getMemberTaxOverview = () => memberJson(`${base}/me`);
export const getMemberDocuments = (year) => memberJson(`${base}/me/documents?year=${Number(year)}`);

/** Download through fetch so the legacy Bearer session works too. */
export async function memberDownload(path, fileName) {
  const res = await fetch(`${BASE_URL}${base}/me/${path}`, memberInit());
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

// ---- public W-9-style form (token only)
async function payee(method, token, body) {
  const res = await fetch(`${BASE_URL}${base}/payee/${encodeURIComponent(token)}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.fields = data.fields || null;
    throw err;
  }
  return data;
}
export const getPayeeForm = (token) => payee('GET', token);
export const submitPayeeForm = (token, body) => payee('POST', token, body);

/** Server error text for a failed call, or the fallback. */
export function errorText(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
}

export const money = (cents) => (cents === null || cents === undefined ? '—'
  : `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** "12.50" / "$1,200" → cents, '' → null, garbage → NaN */
export function toCents(v) {
  const s = String(v ?? '').replace(/[$,\s]/g, '');
  if (s === '') return null;
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return NaN;
  return Math.round(Number(s) * 100);
}

// ---- Platform Admin (super admins only): issuing for any tenant
const sa = '/api/super-admin/tax';
export const platformTaxTenants = (year) => getJson(`${sa}/tenants?year=${Number(year)}`);
export const platformTaxTenant = (tenantId, year) => getJson(`${sa}/tenants/${Number(tenantId)}?year=${Number(year)}`);
export const platformStartRun = (tenantId, year, mode) => postJson(`${sa}/runs`, { tenantId: Number(tenantId), year: Number(year), mode });
export const platformCorrect = (id) => postJson(`${sa}/documents/${Number(id)}/correct`, {});
export const platformDocumentUrl = (id, inline = false) => `${BASE_URL}${sa}/documents/${Number(id)}${inline ? '?inline=1' : ''}`;
